// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { CustomArticleUtils } from '@article/util/custom-article-utils';
import { ProjectModel } from '../../../model/project.model';
import { ProjectContentPresenter } from '@shared/presenter/project-content.presenter';
import { ProjectOptionUtils } from '../util/project-option.utils';
import { ProjectOptionDefIds } from '../constants/project-option-def-ids';
import { PropertyVO } from '../../article/model/vo/property.vo';
import { ProjectUtils } from '../util/project-utils';
import { ArticleNode } from '../../article/presenter/article-node';
import { ArticleTypeIds } from '@article/constants/article-type-ids';
import { PropertyUtils } from '@article/util/property-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { StringUtils } from '@shared/util/string-utils';

export type AssemblyRobotExport = {
  Name: string,
  ID: string,
  ExportDate: string,
  ProjectName: string,
  Comment: string,
  CreatorName: string,
  CreatorEmail: string,
  itemsData: Array<{
    ArticleNumber: string,
    Type: number,
    Rotation: number,
    MountingPoint: {
      X: number,
      Y: number,
      Z: number,
    }
  }>
}

@Injectable({
  providedIn: 'root'
})
export class ExportToCabinetAssemblyRobotService {

  constructor(
    private projectModel: ProjectModel,
    public projectContentPresenter: ProjectContentPresenter) {
  }

  public downloadJson() {
    const jsonObject = this.getJsonObject();
    this.download(this.getFileName(jsonObject), JSON.stringify(jsonObject, null, 2))
  }

  private getJsonObject(): AssemblyRobotExport {
    const configuration = this.projectModel.currentProject;
    const items = this.projectContentPresenter.articleNodeList;
    let creator: string = ProjectOptionUtils.getValueFromProjectOptions(this.projectModel.currentProject.options, ProjectOptionDefIds.CREATED_BY) || "unknown";

    const mountingRail = {
      ArticleNumber: this.getArticleNumber(items[0]),
      Type: items[0].article.type.id,
      Rotation: this.getRotation(items[0].articleUsage.infoProperties),
      MountingPoint: this.getMountingPoint(items[0].articleUsage.infoProperties),
    }

    const mountingRailChildArticles = items[0].children
      .filter(article => this.filterBlockingZones(article))
      .map(article => {
      return {
        ArticleNumber: this.getArticleNumber(article),
        Type: article.article.type.id,
        Rotation: this.getRotation(article.articleUsage.infoProperties),
        MountingPoint: this.getMountingPoint(article.articleUsage.infoProperties),
      }
    });

    return {
      Name: configuration.name,
      Comment: ProjectUtils.getProjectComment(this.projectModel.currentProject),
      ID: StringUtils.createUuid(),
      ExportDate: new Date().toISOString(),
      ProjectName:  configuration.projectGroup.groupName,
      CreatorEmail: creator,
      CreatorName: creator,
      itemsData: [mountingRail, ...mountingRailChildArticles],
    }
  }

  private getArticleNumber(article: ArticleNode) {
    return CustomArticleUtils.getCustomArticleNumber(article.articleUsage) ?? article.articleNo;
  }

  public filterBlockingZones(articleNode: ArticleNode) {
    const typeId = articleNode.article.type.id;
    return ![ArticleTypeIds.BLOCKING_ZONE, ArticleTypeIds.EMPTY_ZONE].includes(typeId);
  }

  private getMountingPoint(props: PropertyVO[]): { X: number, Y: number, Z: number } {
    const x = PropertyUtils.getFloatPropertyValue(props, "positionX");
    const y = PropertyUtils.getFloatPropertyValue(props, "positionY");
    const z = PropertyUtils.getFloatPropertyValue(props, "positionZ");

    return {
      X: NumberUtils.getRoundedValueInMillimeters(+x),
      Y: NumberUtils.getRoundedValueInMillimeters(+y),
      Z: NumberUtils.getRoundedValueInMillimeters(+z),
    }
  }

  private getRotation(props: PropertyVO[]): number {
    let rotY = PropertyUtils.getFloatPropertyValue(props, "rotationY");

    /* Most articles in mountingRail projects are rotated either -90 or 90 degrees.
    The cabinet assembly robots needs these rotations as either 0 or 180 degrees, so we do this: */
    rotY = Math.round(rotY);
    if (rotY >= -91 && rotY <= -89) rotY = 0;
    if (rotY >= 89 && rotY <= 91) rotY = 180;
    return rotY;
  }

  private getFileName(jsonObject: AssemblyRobotExport) {
    return `${jsonObject.Name}_${jsonObject.ID}.json`
  }

  private download(filename: string, text: string) {
    const element = document.createElement('a');
    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(text));
    element.setAttribute('download', filename);

    element.style.display = 'none';
    document.body.appendChild(element);

    element.click();

    document.body.removeChild(element);
  }
}
